import { useCallback, useRef } from 'react';
import { useAPI } from './useAPIs';
import { getSettingValue } from '../utils/CommonUtils';


const urlCache = new Map();
const CACHE_DURATION = 19 * 60 * 1000;

const BLOB_ACCOUNT = getSettingValue('REACT_APP_BLOB_ACCOUNT');
const isBlobStorageUrl = (url) => url && url.includes(BLOB_ACCOUNT);

const checkForAccessError = (response) => {
  const data = response?.data;
  if (data?.error) {
    throw new Error(data.error);
  }
};

const getCachedUrl = (filePath) => {
  const cached = urlCache.get(filePath);
  if (cached && Date.now() < cached.expiry) {
    return cached.url;
  }
  urlCache.delete(filePath);
  return null;
};

const setCachedUrl = (filePath, url) => {
  urlCache.set(filePath, {
    url,
    expiry: Date.now() + CACHE_DURATION
  });
};

export const useFileApi = () => {
  const { apiCall } = useAPI();
  const pendingRequests = useRef(new Map());


  const getDownloadUrl = useCallback(async (filePath) => {
    if (!filePath) {
      throw new Error('File path is required');
    }

    const cachedUrl = getCachedUrl(filePath);
    if (cachedUrl) {
      return cachedUrl;
    }

    if (pendingRequests.current.has(filePath)) {
      return pendingRequests.current.get(filePath);
    }

    const requestPromise = (async () => {
      try {
        const response = await apiCall('post', 'api/download-link/', { file_path: filePath });
        
        checkForAccessError(response);
        
        const downloadUrl = response?.data?.download_url;
        
        if (!downloadUrl) {
          throw new Error('No download URL returned from API');
        }
        if (isBlobStorageUrl(downloadUrl)) {
          setCachedUrl(filePath, downloadUrl);
        }
        return downloadUrl;
      } finally {
        pendingRequests.current.delete(filePath);
      }
    })();

    pendingRequests.current.set(filePath, requestPromise);
    return requestPromise;
  }, [apiCall]);


  const getUploadUrl = useCallback(async (filePath) => {
    if (!filePath) {
      throw new Error('File path is required');
    }

    const response = await apiCall('post', 'api/upload-link/', { file_path: filePath });
    
    checkForAccessError(response);
    
    const uploadUrl = response?.data?.upload_url;
    
    if (!uploadUrl) {
      throw new Error('No upload URL returned from API');
    }

    return uploadUrl;
  }, [apiCall]);

  
  const generateDownloadToken = useCallback(async (files) => {
    if (!files || files.length === 0) {
      throw new Error('At least one file is required');
    }

    const response = await apiCall('post', 'api/generate-download-token/', { files });
    
    checkForAccessError(response);
    
    const token = response?.data?.token;
    
    if (!token) {
      throw new Error('No token returned from API');
    }

    return token;
  }, [apiCall]);


  const resolveDownloadToken = useCallback(async (token) => {
    if (!token) {
      throw new Error('Token is required');
    }

    const response = await apiCall('get', `api/resolve-download-token/${token}/`);
    
    checkForAccessError(response);
    
    const files = response?.data?.files;
    
    if (!files || !Array.isArray(files)) {
      throw new Error('Invalid response from resolve token API');
    }

    files.forEach(file => {
      if (file.path && file.url) {
        if (isBlobStorageUrl(file.url)) {
          setCachedUrl(file.path, file.url);
        }
      }
    });

    return files;
  }, [apiCall]);

 
  const getMultipleDownloadUrls = useCallback(async (filePaths) => {
    if (!filePaths || filePaths.length === 0) {
      throw new Error('At least one file path is required');
    }

    if (filePaths.length === 1) {
      const url = await getDownloadUrl(filePaths[0]);
      return [{ path: filePaths[0], url }];
    }

    const cachedResults = [];
    const uncachedPaths = [];

    for (const path of filePaths) {
      const cachedUrl = getCachedUrl(path);
      if (cachedUrl) {
        cachedResults.push({ path, url: cachedUrl });
      } else {
        uncachedPaths.push(path);
      }
    }

    if (uncachedPaths.length === 0) {
      return cachedResults;
    }

    const token = await generateDownloadToken(uncachedPaths);
    const resolvedFiles = await resolveDownloadToken(token);

    return [...cachedResults, ...resolvedFiles.map(f => ({ path: f.path, url: f.url }))];
  }, [getDownloadUrl, generateDownloadToken, resolveDownloadToken]);


  const invalidateCache = useCallback((filePath) => {
    if (filePath) {
      urlCache.delete(filePath);
    }
  }, []);

  const clearCache = useCallback(() => {
    urlCache.clear();
  }, []);

  return {
    getDownloadUrl,
    getUploadUrl,
    generateDownloadToken,
    resolveDownloadToken,
    getMultipleDownloadUrls,
    invalidateCache,
    clearCache
  };
};
